import { QueryCache, MutationCache, QueryClient } from "@tanstack/react-query";
import { reportError } from "./error-reporting";
import { ErrorWrapper } from "service/api/estabrookFetcher";
import { toast } from "react-toastify";

function isErrorWrapper(error: unknown): error is ErrorWrapper<undefined> {
  return (error as ErrorWrapper<undefined>)?.errors !== undefined;
}

/**
 * `meta` for mutations that report their own failures — e.g. a batched save
 * that tolerates an "already deleted" response. Without it the cache below
 * toasts every rejection, on top of whatever the caller decided to show.
 */
export const skipGlobalErrorToast = { skipGlobalErrorToast: true };

export const queryClient = new QueryClient({
  mutationCache: new MutationCache({
    onError: (error, variables, _context, mutation) => {
      reportError(error, {
        extra: { mutationKey: mutation.options.mutationKey, variables },
      });

      if (mutation.meta?.skipGlobalErrorToast) return;

      let message = "Something went wrong";
      if (isErrorWrapper(error) && error?.errors?.length) {
        message = error.errors[0].detail;
      }
      toast.error(message);
    },
  }),
  defaultOptions: {
    queries: {
      retry: false,
      refetchOnWindowFocus: false,
      staleTime: 2 * 60 * 1000,
    },
  },
  queryCache: new QueryCache({
    onError: (error, query) => {
      reportError(error, {
        extra: { queryKey: query.queryKey },
      });

      let message = "Something went wrong";
      if (isErrorWrapper(error) && error?.errors?.length) {
        message = error.errors[0].detail;
      }
      toast.error(message);
    },
  }),
});
